import { apiErrorMessage, type ApiErrorInput } from '~/lib/apiError';
import { toolboxApi, unwrapResult } from '~/modules/query/base';

/** Result of a DNS / email-setup / SSL call; the shape differs per endpoint. */
export type UtilityResult = Record<string, unknown>;

export type SiteType = string;

export interface ConfigureDnsArgs {
  domainName: string;
  siteType: SiteType;
}

export interface UpsertCnameArgs {
  domainName: string;
  cname: string;
  data: string;
}

export interface EmailSetupArgs {
  method: string;
  domainName: string;
  webhook?: string;
}

export interface DisableSslArgs {
  domain: string;
}

/** The three diagnostics, settled: a lookup's value, or the service's own failure message. */
export interface DnsDiagnosis {
  domain: string;
  /** Public DNS lookups (`root` A, `www` any, `ns`, `resolve` www A), or the lookup's error message. */
  dnsRecords: unknown;
  /** The Rackspace zone `{zoneId, records: {name: {type, data}}}`, null when there is none, or an error message. */
  rackspace: unknown;
  nameservers: unknown;
}

type LookupResult = { data?: unknown; error?: unknown };

// A failed lookup reads as the service's own message (e.g. Rackspace's reason for a 400), not the HTTP status.
const lookupValue = (outcome: PromiseSettledResult<LookupResult>): unknown => {
  if (outcome.status === 'rejected') {
    console.error(outcome.reason);
    return (
      apiErrorMessage(outcome.reason as ApiErrorInput) ??
      'The lookup could not be completed. Try again.'
    );
  }
  if (outcome.value.error)
    return (
      apiErrorMessage(outcome.value.error as ApiErrorInput) ??
      'The lookup could not be completed. Try again.'
    );
  return unwrapResult<unknown>(outcome.value.data);
};

export const utilitiesApi = toolboxApi.injectEndpoints({
  endpoints: (builder) => ({
    // One button runs three independent lookups; a failure in one must not hide the other two.
    diagnoseAll: builder.query<DnsDiagnosis, string>({
      queryFn: async (domain, _api, _extraOptions, baseQuery) => {
        const [records, rackspace, nameservers] = await Promise.allSettled([
          baseQuery({ url: '/diagnostics/dns', method: 'GET', params: { domain } }),
          baseQuery({ url: '/diagnostics/rackspace', method: 'GET', params: { domain } }),
          baseQuery({ url: `/dns/ns/${encodeURIComponent(domain)}`, method: 'GET' }),
        ]);

        return {
          data: {
            domain,
            dnsRecords: lookupValue(records),
            rackspace: lookupValue(rackspace),
            nameservers: lookupValue(nameservers),
          },
        };
      },
    }),

    configureDns: builder.mutation<UtilityResult, ConfigureDnsArgs>({
      query: (body) => ({ url: '/dns/config', method: 'POST', data: body }),
      transformResponse: unwrapResult<UtilityResult>,
    }),

    upsertCname: builder.mutation<UtilityResult, UpsertCnameArgs>({
      query: (body) => ({ url: '/dns/cname', method: 'POST', data: body }),
      transformResponse: unwrapResult<UtilityResult>,
    }),

    // A read, but the card runs it on demand, so it is a mutation rather than a cached query.
    emailSetup: builder.mutation<UtilityResult, EmailSetupArgs>({
      query: ({ method, domainName, webhook }) => ({
        url: '/email-setup',
        method: 'GET',
        params: { provider: 'sendgrid', method, domainName, webhook },
      }),
      transformResponse: unwrapResult<UtilityResult>,
    }),

    disableSsl: builder.mutation<UtilityResult, DisableSslArgs>({
      query: (body) => ({ url: '/sync/disablessl', method: 'POST', data: body }),
      transformResponse: unwrapResult<UtilityResult>,
    }),
  }),
});

export const {
  useLazyDiagnoseAllQuery,
  useConfigureDnsMutation,
  useUpsertCnameMutation,
  useEmailSetupMutation,
  useDisableSslMutation,
} = utilitiesApi;
